import type { ReactNode } from 'react';
import { BookOpen, Calculator, IndianRupee, Layers, School } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';

import { Badge } from '@/components/ui/Badge';
import { FormFieldWide, FormSection } from '@/components/ui/FormSection';
import { Input } from '@/components/ui/Input';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import { MAX_FEE } from '@/features/institute/classes/schemas/class.schema';
import type { AdmissionFormValues } from '../schemas/admission.schema';
import { feeLedger } from '../utils';

/** The money fields, so a fee block cannot be pointed at a name field. */
type MoneyField = Extract<
  keyof AdmissionFormValues,
  | 'tuitionFee'
  | 'tuitionDiscount'
  | 'tuitionPaid'
  | 'booksFee'
  | 'booksDiscount'
  | 'booksPaid'
  | 'otherFee'
  | 'otherDiscount'
  | 'otherPaid'
>;

interface FeeHeadProps {
  form: UseFormReturn<AdmissionFormValues>;
  title: string;
  icon: ReactNode;
  feeField: MoneyField;
  discountField: MoneyField;
  paidField: MoneyField;
  /** Fee less discount — what this head actually comes to. */
  net: number;
  /** Still owed on this head once today's payment is counted. */
  balance: number;
}

/**
 * One head of the bill — tuition, books, or other fees.
 *
 * Written once and used for each: they differ by a label and three field
 * names, which is a prop, not a second copy of the markup (CLAUDE.md §3).
 */
function FeeHead({
  form,
  title,
  icon,
  feeField,
  discountField,
  paidField,
  net,
  balance,
}: FeeHeadProps) {
  const {
    register,
    formState: { errors },
  } = form;

  const money = (field: MoneyField) => ({
    type: 'number' as const,
    inputMode: 'numeric' as const,
    min: 0,
    max: MAX_FEE,
    className: 'font-mono',
    leftIcon: <IndianRupee className="h-4 w-4" />,
    error: errors[field]?.message,
    ...register(field),
  });

  return (
    <div className="rounded-card border border-line bg-surface-2 p-4">
      <h3 className="lbl flex items-center gap-1.5 text-accent">
        {icon}
        {title}
      </h3>

      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Input label="Fee for the year" required {...money(feeField)} />
        <Input label="Concession" required {...money(discountField)} />
        <Input label="Amount Paid" required {...money(paidField)} />
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-line pt-3 text-sm">
        <span className="text-ink-2">
          Payable{' '}
          <strong className="font-mono font-[560] text-ink">{formatCurrency(net)}</strong>
        </span>
        <Badge tone={balance > 0 ? 'warning' : 'success'}>
          {balance > 0 ? `${formatCurrency(balance)} outstanding` : 'Cleared'}
        </Badge>
      </div>
    </div>
  );
}



export interface FeeSectionProps {
  form: UseFormReturn<AdmissionFormValues>;
}

/**
 * What this child is charged, and what has been collected at the desk today.
 *
 * The fees start at whatever the chosen class charges and stay editable,
 * because a concession is granted to a family, not to a class. Every figure is
 * recalculated by the server before a receipt exists — this is the office's
 * running total while it types, nothing more.
 */
export function FeeSection({ form }: FeeSectionProps) {
  const {
    register,
    watch,
    formState: { errors },
  } = form;

  const ledger = feeLedger({
    tuitionFee: watch('tuitionFee'),
    tuitionDiscount: watch('tuitionDiscount'),
    tuitionPaid: watch('tuitionPaid'),
    booksFee: watch('booksFee'),
    booksDiscount: watch('booksDiscount'),
    booksPaid: watch('booksPaid'),
    otherFee: watch('otherFee'),
    otherDiscount: watch('otherDiscount'),
    otherPaid: watch('otherPaid'),
  });

  return (
    <FormSection
      title="F. Fees"
      description="Taken from the class you chose, and adjustable for this child."
      columns={1}
      aside={
        <Badge tone={ledger.totalBalance > 0 ? 'warning' : 'success'}>
          {ledger.totalBalance > 0
            ? `${formatCurrency(ledger.totalBalance)} due`
            : 'Nothing outstanding'}
        </Badge>
      }
    >
      <FeeHead
        form={form}
        title="1. Tuition"
        icon={<School className="h-3.5 w-3.5" aria-hidden />}
        feeField="tuitionFee"
        discountField="tuitionDiscount"
        paidField="tuitionPaid"
        net={ledger.tuitionNet}
        balance={ledger.tuitionBalance}
      />

      <FeeHead
        form={form}
        title="2. Books and study materials"
        icon={<BookOpen className="h-3.5 w-3.5" aria-hidden />}
        feeField="booksFee"
        discountField="booksDiscount"
        paidField="booksPaid"
        net={ledger.booksNet}
        balance={ledger.booksBalance}
      />

      <FeeHead
        form={form}
        title="3. Other fees"
        icon={<Layers className="h-3.5 w-3.5" aria-hidden />}
        feeField="otherFee"
        discountField="otherDiscount"
        paidField="otherPaid"
        net={ledger.otherNet}
        balance={ledger.otherBalance}
      />

      <FormFieldWide>
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-12">
          {/* Ledger to the Left */}
          <div className="lg:col-span-7 xl:col-span-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/60 p-4 shadow-xs">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h3 className="text-xs font-black uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                <Calculator className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <span>Ledger Summary for the Year</span>
              </h3>
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Calculated live</span>
            </div>

            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
              {/* Charged */}
              <div className="rounded-xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 p-2.5 shadow-2xs flex flex-col gap-1">
                <span className="text-xs font-bold text-slate-500 dark:text-slate-400">Total Charged</span>
                <span className="font-mono text-sm font-extrabold text-slate-900 dark:text-white truncate">
                  {formatCurrency(ledger.totalFee)}
                </span>
              </div>

              {/* Concession */}
              <div className="rounded-xl border border-emerald-200/80 dark:border-emerald-900/50 bg-emerald-50/50 dark:bg-emerald-950/30 p-2.5 shadow-2xs flex flex-col gap-1">
                <span className="text-xs font-bold text-emerald-700 dark:text-emerald-400">Concession</span>
                <span className="font-mono text-sm font-extrabold text-emerald-700 dark:text-emerald-300 truncate">
                  {ledger.totalDiscount > 0 ? `− ${formatCurrency(ledger.totalDiscount)}` : '—'}
                </span>
              </div>

              {/* Amount paid */}
              <div className="rounded-xl border border-blue-200/80 dark:border-blue-900/50 bg-blue-50/50 dark:bg-blue-950/30 p-2.5 shadow-2xs flex flex-col gap-1">
                <span className="text-xs font-bold text-blue-700 dark:text-blue-400">Amount Paid</span>
                <span className="font-mono text-sm font-extrabold text-blue-700 dark:text-blue-300 truncate">
                  {formatCurrency(ledger.totalPaid)}
                </span>
              </div>

              {/* Balance */}
              <div className={cn(
                "rounded-xl border p-2.5 shadow-2xs flex flex-col gap-1",
                ledger.totalBalance > 0
                  ? "border-amber-200/80 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200"
                  : "border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900"
              )}>
                <span className={cn(
                  "text-xs font-bold",
                  ledger.totalBalance > 0 ? "text-amber-700 dark:text-amber-400" : "text-slate-500 dark:text-slate-400"
                )}>
                  Balance Due
                </span>
                <span className={cn(
                  "font-mono text-sm font-extrabold truncate",
                  ledger.totalBalance > 0 ? "text-amber-700 dark:text-amber-300" : "text-slate-900 dark:text-white"
                )}>
                  {formatCurrency(ledger.totalBalance)}
                </span>
              </div>
            </div>
          </div>

          {/* Reason and due date to the Right */}
          <div className="flex flex-col gap-4 lg:col-span-5 xl:col-span-4">
            <Input
              label="Reason for the concession (optional)"
              placeholder="Sibling concession, staff ward, RTE"
              autoComplete="off"
              hint="Kept with the record, so a discount can always be accounted for."
              error={errors.concessionReason?.message}
              {...register('concessionReason')}
            />
            {/* The same date "Set the fees" edits on the Fees page later. */}
            <Input
              type="date"
              label="Fees due by (optional)"
              hint="Lists this family under “due soon” on the Fees page. Leave blank to follow the class’s installment dates, if it has any."
              error={errors.feeDueDate?.message}
              {...register('feeDueDate')}
            />
          </div>
        </div>
      </FormFieldWide>
    </FormSection>
  );
}
